"use client";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Field, Input, Textarea, FormStatus, SubmitButton } from "@/components/ui/Form";
import { sendPrivateMessage } from "@/lib/api/c-private-messages";
import styles from "./NewMessageForm.module.css";

/**
 * Form do wysłania nowej wiadomości prywatnej. MVP: single recipient via login
 * (multi-recipient picker w przyszłej iteracji).
 *
 * Backend endpoint: `POST /api/iiapi/c-private-messages/send` ->
 * `C_PRIVATE_MESSAGES_CONTROLLER::IAPI_send`.
 *
 * Walidacja (client + server):
 *   - `to_login` required (login odbiorcy)
 *   - `title` required, max 255
 *   - `text` required (plain text MVP — HTML editor w przyszłej iteracji)
 */
const schema = z.object({
  to_login: z.string().min(1, "Podaj login odbiorcy."),
  title: z.string().min(1, "Podaj tytuł.").max(255, "Tytuł zbyt długi (max 255 znaków)."),
  text: z.string().min(1, "Wpisz treść wiadomości."),
});

type FormValues = z.infer<typeof schema>;

/**
 * Wyciąga login odbiorcy z hash extras `,to=<login>` (URL-encoded).
 * Format: `#extension-new-message,to=jan.kowalski` -> `"jan.kowalski"`.
 * Zwraca `null` gdy brak fragmentu `to=`.
 */
function parseRecipientFromHash(hash: string): string | null {
  if (!hash) return null;
  const stripped = hash.startsWith("#") ? hash.slice(1) : hash;
  for (const piece of stripped.split(",")) {
    if (piece.startsWith("to=")) {
      const val = decodeURIComponent(piece.slice("to=".length));
      return val.trim() || null;
    }
  }
  return null;
}

export function NewMessageForm({ onSent }: { onSent?: () => void } = {}) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: { to_login: "", title: "", text: "" },
  });
  const [topError, setTopError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  /* Prefill odbiorcy z hash extras `,to=<login>` (np. z linku "Wyślij wiadomość"
     w gridzie listy użytkowników: `#extension-new-message,to=jan`). Parsujemy
     hash po mount (client-only) i wstawiamy login do pola `to_login`. */
  useEffect(() => {
    const to = parseRecipientFromHash(window.location.hash);
    if (to) setValue("to_login", to);
  }, [setValue]);

  const onSubmit = async (values: FormValues) => {
    setTopError(null);
    setSuccess(null);
    try {
      await sendPrivateMessage(values);
      setSuccess(`Wiadomość do "${values.to_login}" została wysłana.`);
      reset();
      onSent?.();
    } catch (e) {
      setTopError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Field label="Do (login odbiorcy)" required error={errors.to_login?.message}>
        <Input
          type="text"
          autoComplete="off"
          disabled={isSubmitting}
          placeholder="np. jan.kowalski"
          {...register("to_login")}
        />
      </Field>

      <Field label="Tytuł" required error={errors.title?.message}>
        <Input
          type="text"
          maxLength={255}
          disabled={isSubmitting}
          {...register("title")}
        />
      </Field>

      <Field label="Treść wiadomości" required error={errors.text?.message}>
        <Textarea
          rows={8}
          disabled={isSubmitting}
          {...register("text")}
        />
      </Field>

      {topError && <FormStatus variant="error">{topError}</FormStatus>}
      {success && <FormStatus variant="success">{success}</FormStatus>}

      <div className={styles.actions}>
        <SubmitButton loading={isSubmitting}>Wyślij wiadomość</SubmitButton>
      </div>
    </form>
  );
}
